import React, {memo, useMemo, useCallback} from 'react';
import {StyleSheet, View, FlatList} from 'react-native';
import Styles from '../../Styles';
import {CampusLocationType} from 'src/types/CampusLocationTypes';
import {IsConnectedProps} from 'src/types/Screen';
import {getDescriber} from './descriptor/DescriptorType';
import Localized from 'src/constants/AppStrings';
import HomeScreenTitles from 'src/components/elements/home/HomeScreenTitles';
import NewScanAndPayLocationView from 'src/components/elements/home/NewScanAndPayLocationView';

type ScanAndPayLocation = {
  location: CampusLocationType;
  isCoffeeMachine: boolean;
};

type ScanAndPayLocationsListProps = IsConnectedProps & {
  locations: CampusLocationType[];
  coffeeLocations: CampusLocationType[];
  onLocationClick: (
    machine: CampusLocationType,
    autoAdvanced?: boolean,
  ) => Promise<void>;
  scrollEnabled?: boolean;
  isUnSupportedLocation?: boolean;
};

const {getScanAndPayTile, getScanAndPayMismatchLocationError} = getDescriber();

const NewScanAndPayLocationsList = memo(
  (props: ScanAndPayLocationsListProps) => {
    //Returns Scan And Pay Location Item UI
    const locationList = useMemo<ScanAndPayLocation[]>(() => {
      return [
        ...props.locations.map((loc) => ({
          location: loc,
          isCoffeeMachine: false,
        })),
        ...props.coffeeLocations.map((loc) => ({
          location: loc,
          isCoffeeMachine: true,
        })),
      ];
    }, [props.locations, props.coffeeLocations]);

    const renderItem = useCallback(
      ({item}: {item: ScanAndPayLocation}) => (
        <NewScanAndPayLocationView
          location={item.location}
          onLocationClick={props.onLocationClick}
          isCoffeeMachine={item.isCoffeeMachine}
          isOnlyTile={locationList.length === 1}
        />
      ),
      [props.onLocationClick],
    );

    const renderHeaderItem = useCallback(() => {
      if (props.isUnSupportedLocation) {
        return (
          <View style={styles.listHeader}>
            {getScanAndPayMismatchLocationError()}
          </View>
        );
      }
      return null;
    }, [props.isUnSupportedLocation]);

    return (
      <View style={[styles.container, getScanAndPayTile().listContainer]}>
        <HomeScreenTitles
          label={Localized.Labels.scan_product_title}
          isConnected
          style={{marginLeft: Styles.Spacing.m2}}
        />
        {renderHeaderItem()}
        <FlatList
          renderItem={renderItem}
          style={styles.scanAndPayLocationListView}
          scrollEnabled={true}
          data={locationList}
          horizontal={true}
          keyExtractor={(item, index) =>
            `${item?.location?.locationId}-${index}`
          }
        />
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'flex-start',
    marginBottom: Styles.Spacing.m1,
    marginHorizontal: Styles.Spacing.m2,
    justifyContent: 'flex-start',
  },
  scanAndPayLocationListView: {
    width: '100%',
    marginTop: Styles.Spacing.m3,
  },
  listHeader: {
    marginHorizontal: Styles.Spacing.m3,
  },
});

export default NewScanAndPayLocationsList;
